import RoutePath from 'App/RoutePath';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { useSubdomainInfo } from 'LoggedOut/SignUp/useSubdomainInfo';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import { useRedirectIfVerificationFlowDisabled } from 'LoggedOut/SignUp/useRedirectIfVerificationFlowDisabled';
import { useGetRegistrationMethods } from './hooks/useGetRegistrationMethods';
import { TypedRegistrationFlow } from './TypedRegistrationFlow';
import { useVerifyUserForRegistration } from './hooks/useVerifyUserForRegistration';
import {
  AccountVerificationSubRoutes,
  RegistrationFlow,
  RegistrationMethod,
} from './constants';
import { RegistrationFlowState } from './types';

const getVerificationFlow = (
  registrationMethods: RegistrationMethod[],
): {
  verificationFlow:
    | RegistrationFlow.VERIFICATION_CLOSED_EMPLOYEE_ID
    | RegistrationFlow.VERIFICATION_OPEN_EMAIL;
  registrationMethod:
    | RegistrationMethod.OPEN_EMAIL
    | RegistrationMethod.CLOSED_EMPLOYEE_ID;
} => {
  if (registrationMethods.includes(RegistrationMethod.CLOSED_EMPLOYEE_ID)) {
    return {
      verificationFlow: RegistrationFlow.VERIFICATION_CLOSED_EMPLOYEE_ID,
      registrationMethod: RegistrationMethod.CLOSED_EMPLOYEE_ID,
    };
  }

  return {
    verificationFlow: RegistrationFlow.VERIFICATION_OPEN_EMAIL,
    registrationMethod: RegistrationMethod.OPEN_EMAIL,
  };
};

export const RegistrationAccountVerification = () => {
  const { registrationMethods } = useGetRegistrationMethods();
  const { groupId: organisationId } = useSubdomainInfo({
    subdomain: getSubdomainFromUrl(),
  });
  const { t: translate } = useTranslation('shared');
  const [error, setError] = useState<string | undefined>();
  const [redirectState, setRedirectState] = useState({
    email: '',
    employeeId: '',
    redirectTo: '',
  });
  const history = useHistory();
  const location = useLocation();

  useRedirectIfVerificationFlowDisabled();

  const parentPath = RoutePath.AccountVerification;
  const { registrationMethod, verificationFlow } =
    getVerificationFlow(registrationMethods);

  type K = typeof verificationFlow;

  const isSSOOnlySignUp =
    registrationMethods.length === 1 &&
    registrationMethods.includes(RegistrationMethod.SSO);

  if (isSSOOnlySignUp) {
    history.push(RoutePath.LoginWithSSO);
  }

  const [initialFlowDetails, setInitialFlowDetails] =
    useState<RegistrationFlowState[K]>();

  const {
    callVerifyUserForRegistration,
    loading: verifyUserForRegistrationLoading,
  } = useVerifyUserForRegistration({
    onError: () => setError(translate('errors.messages.oops_something_wrong')),
    onSuccess: () => {
      history.push({
        pathname: RoutePath.EmailConfirmation,
        state: redirectState,
      });
    },
  });

  const getRedirectPath = () => {
    if (verificationFlow === RegistrationFlow.VERIFICATION_CLOSED_EMPLOYEE_ID) {
      return `${parentPath}/${AccountVerificationSubRoutes.Email}`;
    }

    return parentPath;
  };

  useEffect(() => {
    const { email, employeeId } = location.state || {};
    setInitialFlowDetails({ email, employeeId });
  }, [location.state]);

  if (!verificationFlow) return null;

  const onVerificationFlowComplete = async (args: RegistrationFlowState[K]) => {
    if (!organisationId) return;

    const { email } = args;
    const employeeId = 'employeeId' in args ? args.employeeId : '';

    setRedirectState({ email, employeeId, redirectTo: getRedirectPath() });

    await callVerifyUserForRegistration({
      employeeId,
      email,
      registrationMethod,
      organisationId,
    });
  };

  return (
    <TypedRegistrationFlow
      loading={verifyUserForRegistrationLoading}
      registrationFlow={verificationFlow}
      error={error}
      onEnd={onVerificationFlowComplete}
      initialFlowDetails={initialFlowDetails}
      parentPath={parentPath}
    />
  );
};
